import React, { useEffect, useState, useRef } from "react";
import { Box, Dialog, Typography, LinearProgress } from "@mui/material";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";

const ForceInviteSplash = ({
  open,
  title,
  message,
  gameName,
  timerSeconds = 15,
  onComplete,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(timerSeconds);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      completedRef.current = false;
      return;
    }
    completedRef.current = false;
    setSecondsLeft(timerSeconds);
  }, [open, timerSeconds]);

  useEffect(() => {
    if (!open) return undefined;

    if (secondsLeft <= 0) {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
      return undefined;
    }

    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [open, secondsLeft, onComplete]);

  const progress =
    timerSeconds > 0 ? ((timerSeconds - secondsLeft) / timerSeconds) * 100 : 100;

  return (
    <Dialog
      fullScreen
      open={open}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(255,0,255,0.22) 0%, transparent 55%)," +
            "radial-gradient(ellipse at 80% 100%, rgba(0,255,255,0.15) 0%, transparent 50%)," +
            "linear-gradient(160deg, #0a0014 0%, #1a0030 45%, #0d0020 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          px: { xs: 3, sm: 5 },
          maxWidth: 560,
          animation: "forceInviteFadeIn 0.6s ease-out",
          "@keyframes forceInviteFadeIn": {
            from: { opacity: 0, transform: "translateY(24px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 88,
            height: 88,
            borderRadius: "50%",
            mb: 3,
            background:
              "linear-gradient(135deg, rgba(255,0,255,0.25) 0%, rgba(0,255,255,0.25) 100%)",
            border: "2px solid rgba(255,0,255,0.5)",
            boxShadow: "0 0 40px rgba(255,0,255,0.35)",
          }}
        >
          <CelebrationOutlinedIcon sx={{ fontSize: 44, color: "#ff66ff" }} />
        </Box>

        <Typography
          variant="overline"
          sx={{
            color: "#00ffff",
            letterSpacing: 3,
            fontWeight: 700,
            display: "block",
            mb: 1,
          }}
        >
          SPECIAL INVITE
        </Typography>

        <Typography
          variant="h4"
          sx={{
            color: "#fff",
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "1.75rem", sm: "2.25rem" },
            lineHeight: 1.2,
            textShadow: "0 0 30px rgba(255,0,255,0.4)",
          }}
        >
          {title || "You're Invited to Join the Game!"}
        </Typography>

        {gameName ? (
          <Typography
            variant="h6"
            sx={{
              color: "#ff99ff",
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: "1rem", sm: "1.15rem" },
            }}
          >
            {gameName}
          </Typography>
        ) : null}

        <Typography
          variant="body1"
          sx={{
            color: "rgba(255,255,255,0.88)",
            mb: 4,
            lineHeight: 1.7,
            fontSize: { xs: "1rem", sm: "1.1rem" },
            whiteSpace: "pre-wrap",
          }}
        >
          {message ||
            "The host has picked something special just for you. Get ready — the fun is about to begin!"}
        </Typography>

        <Box sx={{ width: "100%", maxWidth: 320, mx: "auto" }}>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.65)", mb: 1 }}
          >
            Joining in {secondsLeft} second{secondsLeft !== 1 ? "s" : ""}…
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "rgba(255,255,255,0.1)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background: "linear-gradient(90deg, #ff00ff 0%, #00ffff 100%)",
              },
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default ForceInviteSplash;
